import React, { useState } from 'react';
import { Users, Info, DollarSign, Briefcase, Percent, Save, ShieldCheck, Download } from 'lucide-react';
import { downloadCSV, downloadXLSX, formatSAPDate } from '../utils/exportUtils';
import { formatCurrency } from '../utils/formatUtils';

const PersonnelAdmin = ({ users, onUpdateUsers }) => {
  const [editableUsers, setEditableUsers] = useState(users);
  const companies = ['Aecon', 'Pomerleau', 'Stantec', 'CAN'];
  const scopes = ['Design', 'Construction', 'Management'];

  const handleScopeChange = (userId, newScope) => {
    const updated = editableUsers.map(u => 
      u.id === userId ? { ...u, roleScope: newScope } : u
    );
    setEditableUsers(updated);
  };

  const handleOverheadChange = (userId, company, value) => {
    const numValue = parseFloat(value) || 0;
    const updated = editableUsers.map(u => 
      u.id === userId ? { 
        ...u, 
        overheads: { ...u.overheads, [company]: numValue } 
      } : u
    );
    setEditableUsers(updated);
  };

  const handleRateChange = (userId, value) => {
    const numValue = parseFloat(value) || 0;
    const updated = editableUsers.map(u => 
      u.id === userId ? { ...u, hourlyRate: numValue } : u
    );
    setEditableUsers(updated);
  };

  const calculateTotalOverhead = (overheads) => {
    return Object.values(overheads).reduce((sum, val) => sum + val, 0);
  };

  const calculateTotalValue = (rate, overheads) => {
    const totalPercentage = calculateTotalOverhead(overheads);
    // Simple total percentage times the base rate
    return rate * (totalPercentage / 100);
  };

  const handleSave = () => {
    onUpdateUsers(editableUsers);
  };

  const exportPersonnelData = () => {
    const headers = ['PERNR', 'ENAME', 'KOSTL', 'ORGEH', 'PLANS', 'HOURLY_RATE'];
    const rows = editableUsers.map(user => [
      user.personnelNumber,
      user.name,
      user.costCenter,
      user.company, // Using company as Org Unit for this JV context
      user.position,
      user.hourlyRate
    ]);
    downloadXLSX(headers, rows, `Personnel_Master_Data_${formatSAPDate(new Date())}.xlsx`, 'Personnel');
  };

  return (
    <div className="max-w-7xl mx-auto flex flex-col gap-4">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-indigo-600 p-1.5 rounded-lg text-white">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Personnel & Rates Management</h1>
        </div>
        
        <div className="flex items-start gap-3 p-3 bg-indigo-50/50 border border-indigo-100 rounded-lg">
          <Info className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-indigo-900 leading-tight font-medium">
            Manage designations, emails, and billing. Adjust <span className="font-bold underline">Assigned Scope</span> for WBS access, 
            and <span className="font-bold underline">Overhead %</span> per company for hourly billing value.
            Click <span className="font-bold">Save Changes</span> to apply.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse table-fixed">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="w-48 px-3 py-2 text-[9px] font-black text-slate-500 uppercase tracking-widest">Team Member</th>
                <th className="w-32 px-3 py-2 text-[9px] font-black text-slate-500 uppercase tracking-widest">Email</th>
                <th className="w-24 px-3 py-2 text-[9px] font-black text-slate-500 uppercase tracking-widest">Company</th>
                <th className="w-24 px-3 py-2 text-[9px] font-black text-slate-500 uppercase tracking-widest text-center">Base Rate</th>
                <th className="w-32 px-3 py-2 text-[9px] font-black text-slate-500 uppercase tracking-widest">Assigned Scope</th>
                {companies.map(company => (
                  <th key={company} className="px-2 py-2 text-[9px] font-black text-slate-500 uppercase tracking-widest text-center">
                    {company} %
                  </th>
                ))}
                <th className="w-20 px-3 py-2 text-[9px] font-black text-slate-500 uppercase tracking-widest text-center bg-indigo-50/30">Total OH</th>
                <th className="w-24 px-3 py-2 text-[9px] font-black text-slate-500 uppercase tracking-widest text-right bg-indigo-50/50">Final Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {editableUsers.map((user) => {
                const totalOH = calculateTotalOverhead(user.overheads);
                const totalValue = calculateTotalValue(user.hourlyRate, user.overheads);
                
                return (
                  <tr key={user.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold border border-slate-200 text-[10px]">
                          {user.name.charAt(0)}
                        </div>
                        <div className="truncate">
                          <p className="text-xs font-bold text-slate-900 truncate">{user.name}</p>
                          <p className="text-[9px] text-slate-400 font-bold uppercase truncate">{user.position}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <p className="text-[10px] text-slate-500 font-medium truncate">{user.email}</p>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-tight">
                        {user.company}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-0.5 justify-center">
                        <DollarSign className="w-2.5 h-2.5 text-slate-400" />
                        <input
                          type="number"
                          value={user.hourlyRate}
                          onChange={(e) => handleRateChange(user.id, e.target.value)}
                          className="w-14 bg-white border border-slate-200 text-center text-[10px] font-bold text-slate-700 rounded py-1 px-1 outline-none focus:ring-1 focus:ring-indigo-500 shadow-xs"
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={user.roleScope}
                        onChange={(e) => handleScopeChange(user.id, e.target.value)}
                        className="bg-white border border-slate-200 text-[10px] font-bold text-slate-700 rounded py-1 px-1.5 outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer shadow-xs w-full"
                      >
                        {scopes.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    {companies.map(company => (
                      <td key={company} className="px-1 py-2 text-center">
                        <input
                          type="number"
                          value={user.overheads[company]}
                          onChange={(e) => handleOverheadChange(user.id, company, e.target.value)}
                          className="w-12 bg-white border border-slate-200 text-center text-[10px] font-bold text-slate-700 rounded py-1 outline-none focus:ring-1 focus:ring-indigo-500 shadow-xs"
                        />
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center bg-indigo-50/10">
                      <span className="text-[10px] font-black text-indigo-600">
                        {totalOH}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right bg-indigo-50/20">
                      <span className="text-[11px] font-black text-slate-900">
                        {formatCurrency(totalValue)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="p-3 bg-slate-50 border-t border-slate-200 flex justify-end">
          <div className="flex gap-3">
            <button
              onClick={exportPersonnelData}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-lg text-xs font-bold shadow-md transition-all active:scale-95"
            >
              <Download className="w-3.5 h-3.5 text-indigo-500" />
              Export Master Data (.xlsx)
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-bold shadow-md shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95"
            >
              <Save className="w-3.5 h-3.5" />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonnelAdmin;
